import { createContext, useCallback, useContext } from "react";
import { AxiosStatic } from "axios";
import { EnvironmentConfig } from "@me8eon/config";
import React from "react";
import { useJwtToken } from "@me8eon/react_authentication";
import { JwtTokenFn } from "@me8eon/authentication";

export type UniqueUserDetails = {
    ctry: string;
    CompanyName: string;
    role?: string;
}
export type UniqueUsersConfigFn = (accessToken: JwtTokenFn) => UniqueUsersFn
export type UniqueUsersFn = (email: string, details: UniqueUserDetails) => Promise<void>;

export const UniqueUsersContext = createContext<UniqueUsersConfigFn | undefined>(undefined);

type UniqueUsersProviderProps = {
    children: React.ReactNode;
    uniqueUsers: UniqueUsersConfigFn;
}

export function UniqueUsersProvider({ children, uniqueUsers }: UniqueUsersProviderProps) {
    return <UniqueUsersContext.Provider value={uniqueUsers}>{children}</UniqueUsersContext.Provider>;
}

export function useUniqueUsers(): UniqueUsersFn {
    const uniqueUsersConfigFn = useContext(UniqueUsersContext);
    if (uniqueUsersConfigFn === undefined) throw new Error("useUniqueUsers must be used within a UniqueUsersProvider");
    const accessToken = useJwtToken();
    const uniqueUsers = useCallback(uniqueUsersConfigFn(accessToken), [uniqueUsersConfigFn, accessToken]);
    return uniqueUsers;
}


export const defaultUniqueUsersFn =
    (axios: AxiosStatic, envName: () => string): UniqueUsersConfigFn =>
        (accessTokenFn: JwtTokenFn) =>
            // `email` is accepted for backwards-compatible call signature but ignored: the backend
            async (_email: string, details: UniqueUserDetails): Promise<void> => {
                const token = await accessTokenFn();
                if (!token) {
                    console.error("Called unique users fn without token");
                    return;
                }
                const { ctry, CompanyName, role } = details || {};
                return axios.post(`${EnvironmentConfig.nodeService}/service/unique-users`, {
                    ctry, CompanyName,
                    role: role || "unknown",
                    env: envName(),
                }, {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });
            };
